import type { ApolloClient } from '@apollo/client';
import { gql } from '@apollo/client';
import { NotAuthorizedException } from '@aws-sdk/client-cognito-identity-provider';
import type { CognitoUserSession } from 'amazon-cognito-identity-js';
import * as AWSCognito from 'amazon-cognito-identity-js';
import { createHmac } from 'crypto-browserify';
import { jwtDecode } from 'jwt-decode';
import {
    cloneDeep,
    forEach,
    groupBy,
    isEmpty,
    isFunction,
    isNil,
    map,
    split,
    startsWith,
    toLower,
    trim,
} from 'lodash-es';
import type { ArgumentAction } from 'redux-logic/definitions/action';
import { v4 as uuid } from 'uuid';

import { dateToISOString, toDate } from '@orion/fe-utils';

import { featureFlags } from 'common/featureFlags/featureFlags';
import gqlClient, { getCognitoUser, nonAuthClient } from 'common/graphql/client';
import { AuditCreationEventsEnum, AuditEntityIdEnum } from 'common/log/auditevents';
import { CIAuditLogger } from 'common/log/ci.auditlogger';
import { CILogger } from 'common/log/log.provider';
import Sentry from 'common/sentry/sentry';
import { setBlockLogout } from 'common/utils/block-logout.utils';
import { getStudyIdWithoutVersion } from 'common/utils/common.utils';
import { fetchAllPaginatedQueryItems } from 'common/utils/paginatedQueries';
import { isSponsorUser } from 'common/utils/sponsorUtils';
import oktaAuth from 'module/auth/okta/oktaAuth';

import { CIError, ErrorSource } from '../error/error_util';
import type {
    AuthUserRoleClaim,
    GetPatientDataQuery,
    GetUserPermissionsAndStudySitesQuery,
    StudySite,
    UserLastPage,
    UserStudies,
    UserStudySite,
} from '../graphql/types';
import {
    AuthUserType,
    isSiteViewRole,
    RoleCategoryEnum,
    RoleVariantEnum,
    UserSubType,
    UserType,
} from '../graphql/types';

import { AuthActions } from './actions';
import { AUTH_STAGE, AuthLocalStorageKeys, CognitoAuthErrorCode } from './constants';
import { PASSCODE_STATUSES, type PasscodeState, type PasscodeStatus } from './passcode.types';
import {
    checkPwdAvailableQuery,
    deleteLastPagesOpenedQuery,
    GetCurrentPolicyAPI,
    getPatientData,
    AddSubscriberDetailsQuery,
    getUserPermissionsAndStudySites,
    listStudySites,
    listUserStudiesQuery,
    listUserStudySitesQuery,
    markPwdQuery,
    updateUserLastActiveDate,
    UpdateUserLastPasswordChangedQuery,
} from './queries';
import { revokeBackendSession } from './sessionRevocation';
import { revokeCognitoToken } from './sso/okta.utils';
import type { SsoAccessTokenData, SsoIdTokenData, SsoSessionKeys } from './sso/sso.types';
import type { ActiveStudySite, AuthState, AuthUser, CognitoAuthError } from './types';

function respondentStudySite(user: AuthUser): { studyId?: string; siteId?: string } {
    const study = user.userStudies?.[0];
    if (!study) {
        return {};
    }

    const studyId = getStudyIdWithoutVersion(study.studyId);
    const siteId = study.userStudySites?.[0]?.siteId;

    return { studyId, siteId };
}

export function logLogin(state: AuthState): void {
    const user = state.activeUser;

    if (user.userType === AuthUserType.PATIENT) {
        const { studyId, siteId } = respondentStudySite(user);
        const respondentLabel = user.subType === UserSubType.CAREGIVER ? 'Caregiver' : 'Patient';

        CIAuditLogger.getInstance().log(
            AuditCreationEventsEnum.Login,
            `${respondentLabel} ${user.username} logged into Patient Web`,
            null,
            {
                relatedEntityName: AuditEntityIdEnum.User,
                relatedEntityId: user.username,
                studyId,
                siteId,
                subjectId: user.pk,
            }
        );
        CILogger.getInstance().info(
            { message: `${respondentLabel} user ${user.pk} logged into Patient Web.` },
            { studyId, siteId, subjectId: user.pk }
        );

        return;
    }

    CILogger.getInstance().info(`User ${user.username} logged in.`);
    CIAuditLogger.getInstance().log(AuditCreationEventsEnum.Login, `User ${user.username} logged in`, null, {
        relatedEntityName: AuditEntityIdEnum.User,
        relatedEntityId: user.pk,
    });
}

export function logLogout(state: AuthState): void {
    const user = state.activeUser;
    if (!user) {
        return;
    }

    if (user.userType === AuthUserType.PATIENT) {
        const { studyId, siteId } = respondentStudySite(user);

        CIAuditLogger.getInstance().log(
            AuditCreationEventsEnum.Logout,
            `Patient user ${user.username} logged out of Patient Web`,
            null,
            {
                relatedEntityName: AuditEntityIdEnum.User,
                relatedEntityId: user.username,
                studyId,
                siteId,
                subjectId: user.pk,
            }
        );
        CILogger.getInstance().info(
            { message: `Patient ${user.pk} logout of Patient Web.` },
            { studyId, siteId, subjectId: user.pk }
        );

        return;
    }

    CILogger.getInstance().info(`${user.username} logged out`);
}

export function determineSelectedActiveStudySite(authUser: AuthUser): ActiveStudySite {
    const userStudies = authUser.userStudies || [];
    const prodSupportUserWithAdminAccess =
        featureFlags.fxIntegration && authUser?.roleVClaim?.sk === RoleVariantEnum.ProductSupport;
    const userHasSiteRole = isSiteViewRole(authUser.roleBClaim?.sk || authUser.roleVClaim?.sk);

    const defaultNoStudySite = {
        activeStudy: null,
        activeSite: null,
        activeStudySiteReady: false,
    };

    if (prodSupportUserWithAdminAccess || !userHasSiteRole || userStudies.length === 0) {
        return defaultNoStudySite;
    }

    // Single study case
    if (userStudies.length === 1) {
        const study = userStudies[0];
        const sites = study.userStudySites || [];

        // Auto-select site when user has only one site or comes from handshake
        if (sites.length === 1) {
            return {
                activeStudy: study,
                activeSite: sites[0],
                activeStudySiteReady: true,
            };
        }

        // Sponsor users with multiple sites don't need to specify a site
        if (isSponsorUser(authUser) && sites.length > 1) {
            return {
                activeStudy: study,
                activeSite: null,
                activeStudySiteReady: true,
            };
        }
    }

    return defaultNoStudySite;
}

// TODO: redux state should be updated accordingly instead of
// depending on reload eg. with windows.location.href = '/'
export const resetUserData = async (activeUser: AuthUser) => {
    CILogger.getInstance().info('Resetting user data', {
        pk: activeUser?.pk,
        sk: activeUser?.sk,
        username: activeUser?.username,
        name: activeUser?.name,
    });
    const userRecord = await fillUserRecord(activeUser);
    localStorage.removeItem(AuthLocalStorageKeys.activeStudySite);
    localStorage.setItem('activeUser', JSON.stringify(userRecord));
};

export function mapGraphQLToRespondent(result: GetPatientDataQuery, user: AuthUser): AuthUser {
    const {
        pk,
        sk,
        tn,
        name,
        firstName,
        lastName,
        firstDay,
        parentId,
        email,
        type,
        subType,
        initials,
        version,
        isDeleted,
        securityQuestion1Text,
        securityQuestion2Text,
        securityQuestion3Text,
        acceptedVersionPP,
        screeningIdentifier,
        createdAt,
        armId,
        studyInstanceId,
        studyId,
        siteId,
        preferredLanguage,
        preferredTimezone,
        preferredClockFormat,
        activationDate,
        caregivers,
    } = result.getPatient;
    const { roleBClaim, roleVClaim, dateOfLastPasswordChange, status, roleName, displayName } = result.getUser;
    user.pk = pk;
    user.tn = tn;
    user.sk = sk;
    user.username = name;
    user.displayName = displayName || name;
    user.email = email;
    user.parentId = parentId;
    user.type = type;
    user.subType = subType;
    user.firstName = firstName;
    user.lastName = lastName;
    user.initials = initials;
    user.isDeleted = isDeleted;
    user.version = version;
    user.firstDay = firstDay;
    user.securityQuestion1Text = securityQuestion1Text;
    user.securityQuestion2Text = securityQuestion2Text;
    user.securityQuestion3Text = securityQuestion3Text;
    user.acceptedVersionPP = acceptedVersionPP;
    user.screeningIdentifier = screeningIdentifier;
    user.createdAt = createdAt as Date;
    user.status = status || null;
    user.armId = armId;
    user.studyId = studyId;
    user.studyInstanceId = studyInstanceId;
    user.userStudies.push({ studyId, userStudySites: [{ siteId }] });
    user.roleBClaim = roleBClaim;
    user.roleVClaim = roleVClaim;
    user.permissions = result.getPermissionsByUser.items;
    user.acceptedVersionPP = acceptedVersionPP;
    user.dateOfLastPasswordChange = dateOfLastPasswordChange;
    user.role = roleName;
    user.preferredLanguage = preferredLanguage;
    user.preferredTimezone = preferredTimezone;
    user.preferredClockFormat = preferredClockFormat;
    user.activationDate = activationDate as string;
    user.caregivers = caregivers;

    return user;
}

export async function getUserStudiesAndStudySites(userData: { pk: string }): Promise<UserStudies[]> {
    // fetch all user studies and user study sites 100 at a time to avoid transform error
    const userStudies = await fetchAllPaginatedQueryItems<UserStudies>(listUserStudiesQuery, {
        queryString: 'listUserStudies',

        variables: {
            filter: { sk: { eq: userData.pk }, isDeleted: { eq: 0 } },
        },
        maxItemsPerQuery: 100,
        responseMapper: (data) => data,
    });

    const userStudySites = await fetchAllPaginatedQueryItems<UserStudySite>(listUserStudySitesQuery, {
        queryString: 'listUserStudySites',
        variables: {
            filter: { sk: { eq: userData.pk }, isDeleted: { eq: 0 } },
            sort: { field: 'studyId', direction: 'asc' },
        },
        maxItemsPerQuery: 100,
        responseMapper: (data) => data,
    });

    const studyIdGroupedStudySites = groupBy(userStudySites.items, 'studyId');

    const buildUserStudiesRaw = userStudies?.items || [];

    forEach(buildUserStudiesRaw, (userStudy: UserStudies) => {
        userStudy.userStudySitesRaw = studyIdGroupedStudySites?.[userStudy.studyId] || [];
    });

    return buildUserStudiesRaw;
}

export function fillUserRecord(user: AuthUser): Promise<AuthUser> {
    try {
        const userCopy = cloneDeep(user);

        if (userCopy.userType === AuthUserType.PATIENT) {
            return getGraphQLClient()
                .query({
                    query: getPatientData,
                    variables: { pk: userCopy.pk || userCopy.username },
                    fetchPolicy: 'network-only',
                })
                .then(async (result): Promise<AuthUser> => {
                    if (userCopy.subscriberPk) {
                        return mapGraphQLToRespondent(result.data, await addCurrentPolicy(userCopy));
                    } else {
                        // for user with no subscriber id will not have any privacy policy attached
                        return mapGraphQLToRespondent(result.data, userCopy);
                    }
                })
                .catch((err): AuthUser => {
                    throw CIError.fromGraphQLError(err);
                });
        }

        const userId = userCopy.authenticationMethod !== 'OKTA' ? userCopy.pk || userCopy.username : undefined;

        return getGraphQLClient()
            .query({
                query: getUserPermissionsAndStudySites,
                variables: { pk: userId },
                fetchPolicy: 'network-only',
                context: {
                    generateFields: ['userStudySitesRaw', 'userStudiesRaw'],
                },
            })
            .then(async (result): Promise<AuthUser> => {
                const userData = cloneDeep(result);
                userData.data.getUser.userStudiesRaw = await getUserStudiesAndStudySites(userData?.data?.getUser);
                // If Sponsor user (Monitor and Clinical Trial Lead) have no userStudySites assigned treat it as all the sites are assigned by converting all the study.sites to  userStudy.userStudySites
                if (userData.data.getUser.type === RoleCategoryEnum.Sponsor) {
                    await Promise.all(
                        map(userData.data.getUser.userStudiesRaw, async (userStudy: UserStudies) => {
                            if (isEmpty(userStudy.userStudySitesRaw)) {
                                try {
                                    // fetch all sites for sponsor users with no sites assigned (access to all sites) 100 at a time
                                    const sitesResult = await fetchAllPaginatedQueryItems<StudySite>(listStudySites, {
                                        queryString: 'listStudySites',
                                        variables: {
                                            studyPk: userStudy.studyId,
                                            filter: { isDeleted: { eq: 0 } },
                                        },
                                        maxItemsPerQuery: 100,
                                        responseMapper: (data) => data,
                                    });

                                    const sites = map(sitesResult?.items, (site) => {
                                        return {
                                            ...site,
                                            pk: site.sk,
                                            siteId: site.sk,
                                            siteName: site.displayName,
                                        };
                                    });
                                    userStudy.userStudySitesRaw = sites;
                                } catch (err) {
                                    throw CIError.fromGraphQLError(err);
                                }
                            }
                        })
                    );
                }

                const graphqlEnhancedUser = addGraphQLDataToUser(userData.data, userCopy);

                if (graphqlEnhancedUser.subscriberPk) {
                    const userWithSubscriberDetails = await addSubscriberDetails(graphqlEnhancedUser);

                    return await addCurrentPolicy(userWithSubscriberDetails);
                }

                // for user with no subscriber id will not have any privacy policy attached
                return graphqlEnhancedUser;
            })
            .catch((err): AuthUser => {
                throw CIError.fromGraphQLError(err);
            });
    } catch (err) {
        throw CIError.fromGraphQLError(err);
    }
}

export function persistAuthUsers(state: AuthState): void {
    localStorage.setItem('activeUser', JSON.stringify(state.activeUser));
    localStorage.setItem(AuthLocalStorageKeys.activeStudySite, JSON.stringify(state.activeStudySite));
    sessionStorage.setItem('showToastMsg', 'true');
    try {
        Sentry?.setUser({ id: state.activeUser?.pk, username: state.activeUser?.username }); //adding user pk to the id if active user is present
    } catch (err) {
        Sentry?.captureException(err);
    }
}

export function updateActiveStudySite(state: AuthState): void {
    if (state.activeStudySite !== null) {
        localStorage.setItem(AuthLocalStorageKeys.activeStudySite, JSON.stringify(state.activeStudySite));
    }
}

export function clearPersistedIdentity(): void {
    localStorage.removeItem('activeUser');
    localStorage.removeItem(AuthLocalStorageKeys.activeStudySite);
    setBlockLogout(false);
}

export async function signOutOfOkta({
    endOktaSessionLocally = false,
}: { endOktaSessionLocally?: boolean } = {}): Promise<void> {
    if (endOktaSessionLocally) {
        oktaAuth.tokenManager.clear();
    }

    await oktaAuth.signOut();
}

export function buildAuthUserFromCognitoSession(session: CognitoUserSession): AuthUser {
    const idToken = session.getIdToken().decodePayload();
    const userKeyToken = idToken.UserId;
    const accessToken = session.getAccessToken().decodePayload();

    return {
        pk: split(userKeyToken, ':')[1],
        sk: null,
        tn: 'User',
        sessionId: uuid(),
        authenticationMethod: 'COGNITO',
        subscriberPk: idToken['custom:SubscriberId'],
        userType: idToken['custom:UserType'],
        name: null,
        displayName: null,
        parentId: null,
        firstName: null,
        firstDay: null,
        type: null,
        role: null,
        lastName: null,
        email: idToken.email,
        initials: null,
        username: accessToken.username,
        permissions: [],
        acceptedVersionPP: null,
        currentPolicy: null,
        subType: idToken['custom:SubType'],
        userStudies: [],
        studyId: null,
        preferredClockFormat: null,
        isDeleted: 0,
        version: 0,
        dateOfLastPasswordChange: null,
        passcodeUpdatedAt: null,
        passcodeAttempts: 0,
        passcodeStatus: 'Inactive',
        guidesStatus: null,
        auth: {
            accessToken: session.getAccessToken().getJwtToken(),
            idToken: session.getIdToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
            expiry: session.getIdToken().getExpiration(),
        },
        publicKeys: [],
        subscriberDetails: {
            allowDcf: true,
            name: null,
        },
        preferredTimezone: 'America/New_York', // default timezone is set America
        activationDate: null,
        currentUserSubscriber: {
            roleClaims: [],
        },
    };
}

export function buildAuthUserFromSSOAccessToken(ssoTokens: SsoSessionKeys): AuthUser {
    const { accessToken, idToken, refreshToken } = ssoTokens;

    const accessTokenDecoded = accessToken && jwtDecode<SsoAccessTokenData>(accessToken);

    const idTokenDecoded = idToken && jwtDecode<SsoIdTokenData>(idToken);

    const authUser: AuthUser = {
        pk: idTokenDecoded?.['custom:UserId'] || accessTokenDecoded?.username || idTokenDecoded?.preferred_username,
        sk: 'User',
        tn: 'User',
        sessionId: uuid(),
        authenticationMethod: 'OKTA',
        subscriberPk: idTokenDecoded?.['custom:SubscriberId'],
        userType: idTokenDecoded?.['custom:UserType'],
        name: null,
        displayName: null,
        parentId: idTokenDecoded?.['custom:UserParentId'],
        oldUserId: null,
        firstName: null,
        firstDay: null,
        type: null,
        role: null,
        lastName: null,
        email: idTokenDecoded?.email,
        initials: null,
        username: accessTokenDecoded?.username || idTokenDecoded?.preferred_username,
        permissions: [],
        acceptedVersionPP: null,
        currentPolicy: null,
        subType: null,
        userStudies: [],
        isDeleted: 0,
        preferredClockFormat: null,
        version: 0,
        dateOfLastPasswordChange: null,
        passcodeUpdatedAt: null,
        passcodeAttempts: 0,
        passcodeStatus: 'Inactive',
        guidesStatus: null,
        preferredTimezone: 'America/New_York',
        activationDate: null,
        auth: {
            accessToken,
            idToken,
            refreshToken,
            expiry: idTokenDecoded?.exp,
            iss: idTokenDecoded?.iss,
        },
        publicKeys: [],
        subscriberDetails: {
            allowDcf: true,
            name: null,
        },
        currentUserSubscriber: {
            roleClaims: [],
        },
    };

    return authUser;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getGraphQLClient(): ApolloClient<any> {
    return gqlClient;
}

export const createHMACPaswordHash = (pass: string): string =>
    createHmac('sha256', 'clinicalink').update(pass).digest('hex');

function checkPasswordAvailability(
    userPk: string,
    pwd: string,
    isActivationCode: boolean,
    activationCode?: string | null
): Promise<boolean> {
    try {
        //for forgot password, reset password flow with security questions
        if (activationCode && isActivationCode) {
            return (
                nonAuthClient
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .query<any>({
                        query: checkPwdAvailableQuery,
                        variables: {
                            pk: userPk,
                            pwd: createHMACPaswordHash(pwd),
                            activationCode: activationCode,
                        },
                        fetchPolicy: 'network-only',
                    })
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .then((response): any => {
                        return response.data.checkPasswordAvailabile;
                    })
                    .catch((err): void => {
                        throw CIError.fromGraphQLError(err);
                    })
            );
        } else if (isActivationCode) {
            // for new user activation flow, where activation code is not saved in session
            return new Promise((resolve) => {
                resolve(true);
            });
        } else {
            // for profile page password change flow
            return (
                getGraphQLClient()
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .query<any>({
                        query: checkPwdAvailableQuery,
                        variables: {
                            pk: userPk,
                            pwd: createHMACPaswordHash(pwd),
                        },
                        fetchPolicy: 'network-only',
                    })
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .then((response): any => {
                        return response.data.checkPasswordAvailabile;
                    })
                    .catch((err): void => {
                        throw CIError.fromGraphQLError(err);
                    })
            );
        }
    } catch (err) {
        throw CIError.fromGraphQLError(err);
    }
}

export function markPasswordUsed(userPk: string, newPass: string): Promise<boolean> {
    try {
        return (
            getGraphQLClient()
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .query<any>({
                    query: gql`
                        ${markPwdQuery}
                    `,
                    variables: {
                        pk: userPk,
                        pwd: createHMACPaswordHash(newPass),
                    },
                    fetchPolicy: 'no-cache',
                })
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .then((response): any => {
                    return response;
                })
                .catch((err): void => {
                    CILogger.getInstance().warn(err);
                    throw CIError.fromGraphQLError(err);
                })
        );
    } catch (err) {
        throw CIError.fromGraphQLError(err);
    }
}

let cognitoUser;

/**
 * Handles Password update for current active User
 *
 * @param oldpass Previous Password
 * @param newpass Proposed Password
 *
 * @returns Current session
 */
function cognitoUserChangePassword(oldpass: string, newpass: string): Promise<CognitoUserSession> {
    return new Promise((resolve, reject): void => {
        checkPasswordAvailability(cognitoUser.username, newpass, false)
            .then((passwordAvailable) => {
                if (passwordAvailable) {
                    cognitoUser.changePassword(oldpass, newpass, function (e, result) {
                        if (e) {
                            if (
                                (e.code !== CognitoAuthErrorCode.NETWORK_ERROR &&
                                    e.code !== CognitoAuthErrorCode.NOT_AUTHORIZED &&
                                    e.code !== CognitoAuthErrorCode.PASSWORD_RESET_REQUIRED &&
                                    e.code !== CognitoAuthErrorCode.TOO_MANY_REQUESTS &&
                                    e.code !== CognitoAuthErrorCode.USER_NOT_CONFIRMED &&
                                    e.code !== CognitoAuthErrorCode.USER_NOT_FOUND) ||
                                !e.code
                            ) {
                                CILogger.getInstance().warn(e);
                            } else {
                                CILogger.getInstance().info(e);
                            }

                            e.code = e.code || CognitoAuthErrorCode.INVALID_PARAMETER;

                            reject(CIError.fromCognitoError(e));
                        }
                        if (result === 'SUCCESS') {
                            cognitoUser.getSession(function (err, session) {
                                if (err) {
                                    reject(CIError.fromCognitoError(err));
                                } else {
                                    resolve(session);
                                }
                            });
                        }
                    });
                } else {
                    const err = {
                        code: CognitoAuthErrorCode.PASSWORD_UNAVAILABLE,
                        name: null,
                        statusCode: null,
                        message: `cognito.${CognitoAuthErrorCode.PASSWORD_UNAVAILABLE}`,
                    };
                    reject(CIError.fromCognitoError(err));
                }
            })
            .catch((err) => {
                reject(err);
            });
    });
}

function cognitoChangePassword(
    newpass: string,
    dispatchAction?: (action: ArgumentAction<string, undefined, undefined>) => void,
    resetUserId?: string | null,
    activationCode?: string | null
): Promise<CognitoUserSession> {
    return new Promise((resolve, reject): void => {
        // Prefer the internal userId captured during the reset flow — Cognito can hand back the display
        // User ID instead of the internal pk, which fails the availability lookup with DbNotFound.
        checkPasswordAvailability(resetUserId || cognitoUser?.username, newpass, true, activationCode)
            .then((passwordAvailable) => {
                if (passwordAvailable) {
                    const savedSession = sessionStorage.getItem('sessionId');
                    if (!cognitoUser.Session && savedSession) {
                        cognitoUser.Session = savedSession;
                    }
                    sessionStorage.removeItem('sessionId');
                    if (isFunction(dispatchAction)) {
                        dispatchAction(AuthActions.isNewPasswordRequested(false));
                    }
                    cognitoUser.completeNewPasswordChallenge(newpass, null, {
                        onSuccess: (session: CognitoUserSession): void => {
                            resolve(session);
                        },
                        onFailure: (e: CognitoAuthError): void => {
                            if (
                                (e.code !== CognitoAuthErrorCode.NETWORK_ERROR &&
                                    e.code !== CognitoAuthErrorCode.NOT_AUTHORIZED &&
                                    e.code !== CognitoAuthErrorCode.PASSWORD_RESET_REQUIRED &&
                                    e.code !== CognitoAuthErrorCode.TOO_MANY_REQUESTS &&
                                    e.code !== CognitoAuthErrorCode.USER_NOT_CONFIRMED &&
                                    e.code !== CognitoAuthErrorCode.USER_NOT_FOUND) ||
                                !e.code
                            ) {
                                CILogger.getInstance().warn(e);
                            } else {
                                CILogger.getInstance().info(e);
                            }

                            e.code = e.code || CognitoAuthErrorCode.INVALID_PARAMETER;

                            reject(CIError.fromCognitoError(e));
                        },
                        mfaRequired: (e): void => {
                            reject({ ...e, trigger: AUTH_STAGE.MFA_REQUIRED });
                        },
                        customChallenge: (e: Record<string, unknown>): void => {
                            reject({ ...e, trigger: AUTH_STAGE.CUSTOM_CHALLENGE });
                        },
                        mfaSetup: (e): void => {
                            reject({ ...e, trigger: AUTH_STAGE.MFA_SETUP });
                        },
                    });
                } else {
                    const err = {
                        code: CognitoAuthErrorCode.PASSWORD_UNAVAILABLE,
                        name: null,
                        statusCode: null,
                        message: `cognito.${CognitoAuthErrorCode.PASSWORD_UNAVAILABLE}`,
                    };
                    reject(CIError.fromCognitoError(err));
                }
            })
            .catch((err) => {
                reject(err);
            });
    });
}

const poolData = {
    UserPoolId: process.env.REACT_APP_AWS_COGNITO_USERPOOL_ID,
    ClientId: process.env.REACT_APP_AWS_COGNITO_USERPOOL_CLIENTID,
    Storage: window.localStorage,
};

export function authenticateViaCognito(
    username: string,
    password: string,
    dispatchAction?: (action: ArgumentAction<string, undefined, undefined>) => void
): Promise<CognitoUserSession> {
    const formattedUsername = toLower(trim(username));
    const formattedPassword = trim(password);

    const userPool = new AWSCognito.CognitoUserPool(poolData);
    const authenticationData = { Username: formattedUsername, Password: formattedPassword };
    const authenticationDetails = new AWSCognito.AuthenticationDetails(authenticationData);
    cognitoUser = new AWSCognito.CognitoUser({
        Username: formattedUsername,
        Pool: userPool,
        Storage: window.localStorage,
    });

    return new Promise((resolve, reject): void => {
        cognitoUser.authenticateUser(authenticationDetails, {
            onSuccess: (session: CognitoUserSession): void => {
                resolve(session);
            },
            onFailure: (e: CognitoAuthError): void => {
                // Valid system error conditions should NOT send an error or warning to our logging service

                if (
                    e.code !== CognitoAuthErrorCode.NETWORK_ERROR &&
                    e.code !== CognitoAuthErrorCode.NOT_AUTHORIZED &&
                    e.code !== CognitoAuthErrorCode.PASSWORD_RESET_REQUIRED &&
                    e.code !== CognitoAuthErrorCode.TOO_MANY_REQUESTS &&
                    e.code !== CognitoAuthErrorCode.USER_NOT_CONFIRMED &&
                    e.code !== CognitoAuthErrorCode.USER_NOT_FOUND
                ) {
                    CILogger.getInstance().warn(e);
                } else {
                    CILogger.getInstance().info(e, { username: authenticationDetails.getUsername() });
                }

                reject(CIError.fromCognitoError(e));
            },
            newPasswordRequired: (e: Record<string, unknown>): void => {
                sessionStorage.setItem('sessionId', cognitoUser.Session);
                if (isFunction(dispatchAction)) {
                    dispatchAction(AuthActions.isNewPasswordRequested(true));
                }
                reject({ ...e, trigger: AUTH_STAGE.NEW_PASS_REQUIRED });
            },
            mfaRequired: (e): void => {
                reject({ ...e, trigger: AUTH_STAGE.MFA_REQUIRED });
            },
            totpRequired: (e): void => {
                reject({ ...e, trigger: AUTH_STAGE.TOTP_REQUIRED });
            },
            customChallenge: (e: Record<string, unknown>): void => {
                reject({ ...e, trigger: AUTH_STAGE.CUSTOM_CHALLENGE });
            },
            mfaSetup: (e): void => {
                reject({ ...e, trigger: AUTH_STAGE.MFA_SETUP });
            },
            selectMFAType: (e): void => {
                reject({ ...e, trigger: AUTH_STAGE.SELECT_MFA_TYPE });
            },
        });
    });
}

export function addGraphQLDataToUser(result: GetUserPermissionsAndStudySitesQuery, user: AuthUser): AuthUser {
    const {
        pk,
        tn,
        sk,
        name,
        displayName,
        firstName,
        lastName,
        parentId,
        oldUserId,
        email,
        subType,
        firstDay,
        roleBClaim,
        roleVClaim,
        createdAt,
        type,
        securityQuestion1Text,
        securityQuestion2Text,
        securityQuestion3Text,
        status,
        isDeleted,
        version,
        dateOfLastPasswordChange,
        lastPagesOpened,
        guidesStatus,
        userStudiesRaw,
        publicKeys,
        preferredTimezone,
        currentUserSubscriber,
        fxId,
    } = result.getUser;
    user.pk = pk;
    user.tn = tn;
    user.sk = sk;
    user.name = name;
    user.displayName = displayName || name;
    user.guidesStatus = guidesStatus;
    user.parentId = parentId;
    user.oldUserId = oldUserId;
    user.fxId = fxId;
    user.isDeleted = isDeleted;
    user.version = version;
    user.username = name;
    user.firstName = firstName;
    user.lastName = lastName;
    user.firstDay = firstDay;
    user.email = email;
    user.initials = (firstName ? firstName.charAt(0) : '') + (lastName ? lastName.charAt(0) : '');
    user.roleBClaim = roleBClaim;
    user.roleVClaim = roleVClaim;
    user.createdAt = createdAt;
    user.type = type;
    user.permissions = result.getPermissionsByUser.items;
    user.acceptedVersionPP = result.getUser.acceptedVersionPP;
    user.subType = subType;
    user.userStudies = map(
        userStudiesRaw,
        (userStudy: UserStudies): UserStudies => ({
            ...userStudy,
            userStudySites: userStudy?.userStudySitesRaw || [],
        })
    );
    user.securityQuestion1Text = securityQuestion1Text;
    user.securityQuestion2Text = securityQuestion2Text;
    user.securityQuestion3Text = securityQuestion3Text;
    user.status = status || null;
    user.dateOfLastPasswordChange = !isNil(dateOfLastPasswordChange) ? toDate(dateOfLastPasswordChange) : null;
    user.role = type === 'System' ? RoleVariantEnum.SystemAdministrator : null;
    user.lastPagesOpened = lastPagesOpened;
    user.publicKeys = publicKeys;
    user.preferredTimezone = preferredTimezone;
    // TODO: we should just query roleClaims instead of merging roleVClaims and roleBClaims
    // but Apollo would need some additional setup for it to work and it didn't work for me
    // https://www.apollographql.com/docs/react/data/fragments#using-fragments-with-unions-and-interfaces
    user.currentUserSubscriber = currentUserSubscriber && {
        currentRoleId: currentUserSubscriber.currentRoleId,
        roleClaims: mergeRoleClaims(currentUserSubscriber.roleBClaims, currentUserSubscriber.roleVClaims),
    };

    const parentIdParts = split(parentId, '#');
    for (const parentIdPart of parentIdParts || []) {
        const parentPartLc = toLower(parentIdPart);
        if (startsWith(parentPartLc, 'subscriber.')) {
            user.subscriberPk = parentIdPart;
        }
        if (startsWith(parentPartLc, 'sponsor.')) {
            user.sponsorPk = parentIdPart;
        }
        if (startsWith(parentPartLc, 'customer.')) {
            user.customerPk = parentIdPart;
        }
    }

    return user;
}

const determinePasscodeStatus = (passcodeStatusRaw: unknown, passcodeUpdatedAt: Date | null): PasscodeStatus => {
    if (PASSCODE_STATUSES.includes(passcodeStatusRaw as PasscodeStatus)) {
        return passcodeStatusRaw as PasscodeStatus;
    }

    return passcodeUpdatedAt ? 'Active' : 'Inactive';
};

export const extractPasscodeDataFromUserResult = (userResult: Record<string, unknown>): PasscodeState => {
    const passcodeUpdatedAtRaw = userResult.passcodeUpdatedAt;
    const passcodeUpdatedAt = !isNil(passcodeUpdatedAtRaw) ? toDate(passcodeUpdatedAtRaw as string | Date) : null;

    const passcodeAttemptsRaw = userResult.passcodeAttempts;
    const passcodeAttempts = Number.isFinite(Number(passcodeAttemptsRaw)) ? Number(passcodeAttemptsRaw) : 0;

    return {
        passcodeUpdatedAt,
        passcodeAttempts,
        passcodeStatus: determinePasscodeStatus(userResult.passcodeStatus, passcodeUpdatedAt),
    };
};

//get the currently published policy
function addCurrentPolicy(user: AuthUser): Promise<AuthUser> {
    try {
        const authUser = user;

        return (
            getGraphQLClient()
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .query<any>({
                    query: GetCurrentPolicyAPI,
                    variables: {
                        pk: user.subscriberPk,
                        filter: { status: { eq: 'PUBLISHED' }, isDeleted: { eq: 0 }, language: { eq: 'en-US' } },
                    },
                    fetchPolicy: 'no-cache',
                })
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .then((response): any => {
                    return { ...authUser, currentPolicy: response.data.listSubscriberPPs.items };
                })
                .catch((err): void => {
                    throw CIError.fromGraphQLError(err);
                })
        );
    } catch (err) {
        throw CIError.fromGraphQLError(err);
    }
}

export const mergeRoleClaims = (roleBClaims: AuthUserRoleClaim[], roleVClaims: AuthUserRoleClaim[]) => {
    return [...roleBClaims, ...roleVClaims];
};

interface GetSubscriberResponse {
    getSubscriber: {
        allowDcf: boolean;
        name: string;
    };
}

/**
 * Gets subscriber details
 * @param authUser
 * @returns AuthUser with Subscriber details
 */
function addSubscriberDetails(authUser: AuthUser): Promise<AuthUser> {
    try {
        return (
            getGraphQLClient()
                .query<GetSubscriberResponse>({
                    query: AddSubscriberDetailsQuery,
                    variables: {
                        pk: authUser.subscriberPk,
                    },
                    fetchPolicy: 'network-only',
                })
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .then((response): any => {
                    const authUserWithSubscriberDetails: AuthUser = {
                        ...authUser,
                        subscriberDetails: {
                            allowDcf: response.data.getSubscriber.allowDcf,
                            name: response.data.getSubscriber.name,
                        },
                    };

                    return authUserWithSubscriberDetails;
                })
                .catch((err): void => {
                    throw CIError.fromGraphQLError(err);
                })
        );
    } catch (err) {
        throw CIError.fromGraphQLError(err);
    }
}

function getCognitoSessionOrNull(cognitoUser: AWSCognito.CognitoUser): Promise<CognitoUserSession | null> {
    return new Promise((resolve) => {
        cognitoUser.getSession((err: Error | null, currentSession: CognitoUserSession | null) =>
            resolve(err ? null : currentSession)
        );
    });
}

/**
 * Ends the current Cognito session, in the order required for a reliable end-to-end
 * session termination. Only this session is ended: other devices and browsers keep their sessions.
 *
 * 1. `signOut` AppSync mutation — denylists the session (by `origin_jti`) so AppSync stops
 *    accepting it immediately, independent of Cognito's own token lifetimes.
 * 2. `RevokeTokenCommand` — revokes this session's refresh token and the access tokens issued
 *    from it. A `NotAuthorizedException` for an already-revoked token (e.g. a logout already ran
 *    in another tab) is expected, not an error, and is logged at a lower severity.
 * 3. `cognitoUser.signOut()` — clears the local Cognito session
 *
 * Each step is independently caught so that one failing step never prevents the next from
 * running or blocks the rest of the logout flow.
 * @param authUser - The authenticated user, used for logging
 */
export async function revokeCognitoTokenAndSignOut(authUser: AuthUser): Promise<void> {
    const cognitoUser = getCognitoUser();
    if (!cognitoUser) {
        return;
    }

    try {
        await revokeBackendSession(authUser);

        const session = await getCognitoSessionOrNull(cognitoUser);

        if (session) {
            try {
                await revokeCognitoToken(session.getRefreshToken().getToken());
                CILogger.getInstance().info('Cognito token revoked', { subjectId: authUser?.pk });
            } catch (e) {
                if (e instanceof NotAuthorizedException && /revoked/i.test((e as Error)?.message ?? '')) {
                    CILogger.getInstance().log('RevokeTokenCommand found an already-revoked token', e, {
                        subjectId: authUser?.pk,
                    });
                } else {
                    CILogger.getInstance().error('Failed to revoke Cognito token', e, { subjectId: authUser?.pk });
                    Sentry?.captureException(
                        new CIError(
                            (e as Error)?.message ?? 'Unknown error revoking Cognito token',
                            'cognito.revoketoken',
                            ErrorSource.COGNITO,
                            e
                        )
                    );
                }
            }
        }
    } finally {
        cognitoUser.signOut();
    }
}

async function buildOktaAuthUser(ssoTokens: SsoSessionKeys): Promise<AuthUser> {
    const authUser = buildAuthUserFromSSOAccessToken(ssoTokens);
    localStorage.setItem('activeUser', JSON.stringify(authUser));

    const userRecord = await fillUserRecord(authUser);
    localStorage.setItem('activeUser', JSON.stringify(userRecord));

    return userRecord;
}

async function loginCognito(
    username: string,
    password: string,
    dispatchAction?: (action: ArgumentAction<string, undefined, undefined>) => void
) {
    const session = await authenticateViaCognito(username, password, dispatchAction);
    const authUser = buildAuthUserFromCognitoSession(session);
    const filledAuthUser = await fillUserRecord(authUser);

    // OR-23228 prevent subscriber users go via Patient option (except System admin)
    const canUseCognitoLogin =
        !featureFlags.fxIntegration ||
        filledAuthUser.type === UserType.Patient ||
        filledAuthUser.role === RoleVariantEnum.SystemAdministrator;

    if (canUseCognitoLogin) {
        return filledAuthUser;
    }

    revokeCognitoTokenAndSignOut(filledAuthUser);
    throw CIError.fromCognitoError({
        code: CognitoAuthErrorCode.OKTA_LOGIN_REQUIRED,
        name: 'OktaLoginRequired',
        statusCode: 401,
        message: 'cognito.OktaLoginRequired',
    });
}

export async function login(
    username: string | null,
    password: string | null,
    ssoTokens?: SsoSessionKeys,
    dispatchAction?: (action: ArgumentAction<string, undefined, undefined>) => void
): Promise<AuthUser> {
    if (ssoTokens) {
        const activeUserFromSession = localStorage.getItem('activeUser');
        const activeUser: AuthUser = activeUserFromSession && JSON.parse(activeUserFromSession);

        await revokeCognitoTokenAndSignOut(activeUser);

        return buildOktaAuthUser(ssoTokens);
    }

    return loginCognito(username, password, dispatchAction);
}

/**
 * * Deletes the User.lastPagesOpened, So the next Login would be clean.
 * ! Deleting just from BE. The Current Session State will contain lastPagesOpened until logout.
 * @param authUser Authenticated User
 * @returns Authenticated User
 */
export function deleteLastPagesOpened(authUser: AuthUser): Promise<AuthUser> {
    if (!!authUser.lastPagesOpened && authUser.lastPagesOpened.length > 0) {
        return getGraphQLClient()
            .mutate<{ updateUserLastPages: Array<UserLastPage> }>({
                mutation: deleteLastPagesOpenedQuery,
                variables: {
                    pk: authUser.pk,
                },
            })
            .then(({ data }) => {
                if (data.updateUserLastPages.length === 0) {
                    return authUser;
                } else {
                    //Kibana logging
                    CILogger.getInstance().info(
                        {
                            message: `Unable to delete last opened pages for the user ${authUser.pk}`,
                        },
                        {
                            subjectId: authUser.pk,
                        }
                    );
                    // Commented out as a workaround for OR-18503
                    // throw new Error(`Unable to delete last opened pages for the user ${authUser.pk}`);
                }
            });
        // Commented out as a workaround for OR-18503
        // .catch((err) => {
        //     throw CIError.fromGraphQLError(err);
        // });
    }

    return new Promise((resolve) => {
        resolve(authUser);
    });
}

export function updateUsersLastLoggedInTime(authUser: AuthUser): Promise<AuthUser> {
    return getGraphQLClient()
        .mutate<{ updateUserLastActiveDate: boolean }>({
            mutation: updateUserLastActiveDate,
        })
        .then(({ data }) => {
            if (data.updateUserLastActiveDate) {
                return authUser;
            } else {
                //Kibana logging
                CILogger.getInstance().info(
                    {
                        message: `Unable to update last active date for the ${
                            authUser.type === UserType.Patient ? 'patient' : 'user'
                        } ${authUser.pk}`,
                    },
                    {
                        subjectId: authUser.type === UserType.Patient && authUser.pk,
                    }
                );
                throw new Error(
                    `Unable to update last active date for the ${
                        authUser.type === UserType.Patient ? 'patient' : 'user'
                    } ${authUser.pk}`
                );
            }
        })
        .catch((err) => {
            throw CIError.fromGraphQLError(err);
        });
}

function handleUserLastPasswordChange(toBeResolvedPromise: Promise<AWSCognito.CognitoUserSession>): Promise<AuthUser> {
    return toBeResolvedPromise
        .then((session): AuthUser => buildAuthUserFromCognitoSession(session))
        .then((authUser): Promise<AuthUser> => fillUserRecord(authUser))
        .then((authUser): Promise<AuthUser> => {
            return getGraphQLClient()
                .mutate<{ updateUser: AuthUser }>({
                    mutation: UpdateUserLastPasswordChangedQuery,
                    variables: {
                        pk: authUser?.pk,
                        sk: authUser?.sk,
                        dateOfLastPasswordChange: dateToISOString(new Date(), true),
                        isDeleted: authUser?.isDeleted,
                        expectedVersion: authUser.version,
                    },
                })
                .then(({ data }): AuthUser => {
                    const { version, dateOfLastPasswordChange } = data.updateUser;
                    authUser.version = version;
                    authUser.dateOfLastPasswordChange = toDate(dateOfLastPasswordChange);

                    return authUser;
                });
        });
}

// * Active user changes the password
export function userChangePassword(oldpass: string, newpass: string): Promise<AuthUser> {
    return handleUserLastPasswordChange(cognitoUserChangePassword(oldpass, newpass));
}

export function changePassword(
    newpass: string,
    resetUserId?: string | null,
    activationCode?: string | null
): Promise<AuthUser> {
    return handleUserLastPasswordChange(cognitoChangePassword(newpass, undefined, resetUserId, activationCode));
}
